import { useCallback } from "react";
import { Checkbox, Label, Button, Popover, PopoverContent, PopoverTrigger } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { ChevronDown } from "lucide-react";
import { IcCar, IcLocation } from "@racoon/icons";
import { BUCKETS } from "~/shared/lib/pickup-status/pickup-status.lib";

interface MapFilterProps {
  jobFilters: string[];
  truckFilters: string[];
  onJobFiltersChange: (filters: string[]) => void;
  onTruckFiltersChange: (filters: string[]) => void;
}

export const MapFilter = ({
  jobFilters,
  truckFilters,
  onJobFiltersChange,
  onTruckFiltersChange,
}: MapFilterProps) => {
  const t = useT();

  const jobFilterOptions = BUCKETS.map((b) => ({
    id: b,
    label: t(Namespace.Routes, `pickupStatusTab.${b}`),
  }));

  // Truck filter configuration
  const truckFilterOptions = [
    { id: "with-shift", label: t(Namespace.Enum, "WITH_SHIFT") },
    { id: "with-orders", label: t(Namespace.Enum, "WITH_ORDERS") },
  ];

  // Generic function to toggle a filter
  const toggleFilter = useCallback(
    (filterId: string, filters: string[], setFilters: (filters: string[]) => void) => {
      if (filters.includes(filterId)) {
        setFilters(filters.filter((f) => f !== filterId));
      } else {
        setFilters([...filters, filterId]);
      }
    },
    [],
  );

  // Function to get a human-readable label for the filter button
  const getFilterButtonLabel = useCallback(
    (
      selectedFilters: string[],
      allOptions: Array<{ id: string; label: string }>,
      allLabel: string,
    ): string => {
      if (selectedFilters.length === 0) {
        return allLabel;
      }

      const firstFilterLabel =
        allOptions.find((opt) => opt.id === selectedFilters[0])?.label || selectedFilters[0];

      if (selectedFilters.length === 1) {
        return firstFilterLabel;
      }

      return `${firstFilterLabel} +${selectedFilters.length - 1}`;
    },
    [],
  );

  return (
    <div className="flex gap-2 flex-wrap">
      {/* Locations filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2 bg-white" size="sm">
            <IcLocation className="h-5 w-5 text-brand-blue " />
            {getFilterButtonLabel(
              jobFilters,
              jobFilterOptions,
              t(Namespace.Routes, "pickupStatusTab.allJobs"),
            )}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="!w-48 ">
          <div className="space-y-3">
            <div className="font-medium text-sm">
              {t(Namespace.Routes, "pickupStatusTab.jobsFilterLabel")}
            </div>
            <div className="space-y-2">
              {jobFilterOptions.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`filter-${option.id}`}
                    checked={jobFilters.includes(option.id)}
                    onCheckedChange={() => toggleFilter(option.id, jobFilters, onJobFiltersChange)}
                  />
                  <Label htmlFor={`filter-${option.id}`} className="text-sm cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {jobFilters.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => onJobFiltersChange([])}
              >
                {t(Namespace.Common, "reset")}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Truck filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 bg-white">
            <IcCar className="h-5 w-5 text-brand-blue " />

            {getFilterButtonLabel(
              truckFilters,
              truckFilterOptions,
              t(Namespace.Common, "allTrucks"),
            )}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="!w-48">
          <div className="space-y-3">
            <div className="font-medium text-sm">{t(Namespace.Common, "trucksFilterLabel")}</div>
            <div className="space-y-2">
              {truckFilterOptions.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`truck-${option.id}`}
                    checked={truckFilters.includes(option.id)}
                    onCheckedChange={() =>
                      toggleFilter(option.id, truckFilters, onTruckFiltersChange)
                    }
                  />
                  <Label htmlFor={`truck-${option.id}`} className="text-sm cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {truckFilters.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => onTruckFiltersChange([])}
              >
                {t(Namespace.Common, "reset")}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
